/**
 * @copyright       Palace Resorts
 * @description     Módulo para el manejo de la autenticación y sesión del usuario,
 * Este módulo maneja el STATE para los procesos de autenticación y manejo de la sesión del
 * usuario, utilizando AWS Cognito.
 *
 * Debería implementar las acciónes de SigIn, SignOut.
 *
 * @author          Roberto Pérez Guzmán
 * @creationDate    06 de Mayo del 2021
 */

import { Auth, Hub, Logger } from "aws-amplify";
import jwt_decode from "jwt-decode";
// Se cambia la forma de autentificacion a personalizada
/* Auth.configure({
  authenticationFlowType: "CUSTOM_AUTH",
}); */
const logger = new Logger("auth-logger", process.env.VUE_APP_LOGLEVEL);

export default {
  // El acceso a las propiedades del STATE utiliza el modo de
  // acceso por namespace, es decir, que para acceder a la propiedad
  // se debe seguir el path completo desde el STATE principal y los sub-módulos
  // correspondientes.
  // en este caso "auth/<propiedad>"
  // "auth" es el nombre con el que se registra este módulo en el STATE principal.
  // @see store.js
  namespaced: true,
  // Establecemos el modo estricto para el uso del STATE, lo cual
  // por lo que únicamente los GETTERS y MUTATIONS son los únicos que tienen
  // permitido el acceso las propiedades del STATE de la aplicación.
  strict: true,
  // STATE del módulo
  state: {
    /**
     * Información del usuario
     */
    user: null,
    /**
     * Token del usuario
     */
    token: null,
    /**
     * Roles del usuario
     */
    roles: null,
    /**
     * Rol del usuario (permisos)
     */
    rol: null,
    /**
     * Permisos por usuario
     */
    permissions: null,
    /**
     * Nombre del usuario
     */
    name: null,
    /**
     * Username por ID
     */
    userID: null,
    /**
     * Intentos fallidos por usuario.
     * Para cada usuario se registra
     * {
     *  '<username>':
     *      'lastAttempt': 'fecha de último intento>'
     *      'attempts': 'número de intentos fallidos'
     * }
     */
    failAttempts: {},
    accessToEmail: false,
  },
  getters: {
    /**
     * Determina si el usuario ya ha iniciado sesión o no.
     * @returns true, si el usuario ya ha iniciado sesión, false en caso contrario.
     */
    // Si un usuario ya inició sesión la propiedad user del STATE se encuentra inicializada
    // y es diferente de null
    isAuthenticated: (state: any) => state.user !== null,

    /**
     * Mantiene la información de la sesión del usuario
     */
    user: (state: any) => state.user,
    token: (state: any) => state.token,
    roles: (state: any) => state.roles,
    rol: (state: any) => state.rol,
    permissions: (state: any) => state.permissions,
    name: (state: any) => state.name,
    userID: (state: any) => state.userID,

    failAttempts: (state: any, { username }: any) => {
      if (!username) {
        return null;
      }

      return state.failAttempts[username];
    },

    accessToEmail: (state: any) => state.accessToEmail,
  },
  mutations: {
    user(state: any, { user }: any) {
      logger.debug(`auth.user.commit`);
      state.user = user;
    },

    lastUserAttempSigIn(state: any, { username }: any) {
      logger.debug(`auth.lastUserAttempSigIn.commit`);
      state.lastUserAttempSigIn = username;
    },

    lastUserAttempSendCustomChallengeAnswer(state: any, { username }: any) {
      logger.debug(`auth.lastUserAttempSendCustomChallengeAnswer.commit`);
      state.lastUserAttempSigIn = username;
    },

    token(state: any, { token }: any) {
      logger.debug(`auth.token.commit`);
      state.token = token;
    },

    roles(state: any, { roles }: any) {
      logger.debug(`auth.roles.commit`);
      state.roles = roles;
    },

    rol(state: any, { rol }: any) {
      logger.debug(`auth.rol.commit`);
      state.rol = rol;
    },

    permissions(state: any, { permissions }: any) {
      logger.debug(`auth.permissions.commit`);
      state.permissions = permissions;
    },

    name(state: any, { name }: any) {
      logger.debug(`auth.name.commit`);
      state.name = name;
    },

    userID(state: any, { userID }: any) {
      logger.debug(`auth.userID.commit`);
      state.userID = userID;
    },

    failAttempts(state: any, { failAttempts }: any) {
      logger.debug(`auth.failAttempts.commit`);
      localStorage.setItem("__failAttempts", "");
      state.failAttempts = failAttempts;
    },

    accessToEmail(state: any, access: any) {
      logger.debug(`auth.accessToEmail.commit`);
      state.accessToEmail = access;
    },
  },
  actions: {
    /**
     * Inicializa la información de la sesión del usuario.
     *
     */
    init({ commit, state, dispatch }: any) {
      const sFailAttempts = localStorage.getItem("__failAttempts");
      if (sFailAttempts && sFailAttempts !== "") {
        const failAttempts = JSON.parse(sFailAttempts);
        commit("failAttempts", { failAttempts });
      }

      const listener = async (data: any) => {
        switch (data.payload.event) {
          case "signIn":
            logger.debug("user signed in");
            dispatch("session");
            break;
          case "signUp":
            logger.debug("user signed up");
            break;
          case "sendCustomChallengeAnswer":
            logger.debug("send Custom Challenge Answer");
            break;
          case "signOut":
            logger.debug("user signed out");
            break;
          case "signIn_failure":
            logger.error("user sign in failed");
            logger.error(`lastUserAttempSigIn: ${state.lastUserAttempSigIn}`);
            logger.debug(data);
            //commit('failAttemp', { username })
            break;
          case "tokenRefresh":
            {
              dispatch("session");
            }
            break;
          case "tokenRefresh_failure":
            logger.error("token refresh failed");
            break;
          case "configured":
            logger.debug("the Auth module is configured");
        }
      };

      Hub.listen("auth", listener);

      return Auth.currentAuthenticatedUser()
        .then((user) => {
          commit("user", { user });
          return user;
        })
        .then(() => {
          return Auth.currentSession();
        })
        .then((session) => {
          const token = session.getIdToken().getJwtToken();
          commit("token", { token });
          const tokenDecoded: any = jwt_decode(token);
          const rol = tokenDecoded.Rol;
          const permissions = tokenDecoded.Options;
          const name = tokenDecoded.NombreUsuario;
          const userID = tokenDecoded.ProveedorID;
          if (rol) {
            commit("rol", { rol });
          }
          if (permissions) {
            commit("permissions", { permissions });
          }
          if (name) {
            commit("name", { name });
          }
          if (userID) {
            commit("userID", { userID });
          }
        })
        .catch((e) => {
          logger.warn(e);
          commit("token", { token: null });
          commit("user", { user: null });
          commit("rol", { rol: null });
          commit("permissions", { permissions: null });
          commit("name", { name: null });
          commit("userID", { userID: null });
        });
    },
    signIn({ commit }: any, { username }: any) {
      commit("lastUserAttempSigIn", { username });
      return Auth.signIn(username);
    },
    // eslint-disable-next-line no-empty-pattern
    newPassword({}, { user, newPassword }: any) {
      return Auth.completeNewPassword(user, newPassword);
    },
    signOut({ dispatch }: any) {
      return Auth.signOut()
        .then((data) => {
          dispatch("clearSession");
          logger.debug(data);
        })
        .catch((err) => logger.warn(err));
    },
    session({ commit, dispatch }: any) {
      return Auth.currentSession()
        .then((session) => {
          logger.warn("Session info is OK");
          if (session) {
            const token = session.getIdToken().getJwtToken();
            commit("token", { token });
            logger.debug("token refresh succeeded");
            const tokenDecoded: any = jwt_decode(token);
            if (tokenDecoded.Rol) {
              logger.debug("rol refresh succeeded");
              commit("rol", tokenDecoded.Rol);
            }
            if (tokenDecoded.Options) {
              logger.debug("permissions refresh succeeded");
              commit("permissions", tokenDecoded.Options);
            }
            if (tokenDecoded.NombreUsuario) {
              logger.debug("name refresh succeeded");
              commit("name", tokenDecoded.NombreUsuario);
            }
            if (tokenDecoded.userID) {
              logger.debug("userID refresh succeeded");
              commit("userID", tokenDecoded.userID);
            }
          } else {
            dispatch("clearSession");
            logger.warn("Session info is missing");
          }
        })
        .catch((e) => {
          dispatch("clearSession");
          logger.warn("Session info is missing");
          logger.warn(e);
        });
    },
    clearSession({ commit }: any) {
      commit("user", null);
      commit("token", null);
      commit("roles", null);
      commit("rol", null);
      commit("permissions", null);
      commit("name", null);
      commit("userID", null);
    },
    // eslint-disable-next-line no-empty-pattern
    recoveryPassword({ commit }: any, accessToEmail: any) {
      logger.debug(`auth.accessToEmail.action`);
      logger.debug(accessToEmail);
      commit("accessToEmail", accessToEmail);
      return accessToEmail;
    },
    sendCustomChallengeAnswer({ commit }: any, { user, password }: any) {
      commit("lastUserAttempSendCustomChallengeAnswer", { user });
      return Auth.sendCustomChallengeAnswer(user, password);
    },
  },
};
